import { forwardRef, useLayoutEffect, useRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';

interface AutosizeTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  maxHeight?: number;
}

const AutosizeTextarea = forwardRef<HTMLTextAreaElement, AutosizeTextareaProps>(
  ({ maxHeight = 180, onInput, style, ...props }, forwardedRef) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const resize = () => {
      const element = innerRef.current;
      if (!element) return;
      element.style.height = 'auto';
      const height = Math.min(element.scrollHeight, maxHeight);
      element.style.height = `${height}px`;
      element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
      if (element.scrollHeight > maxHeight) element.scrollTop = element.scrollHeight;
    };

    useLayoutEffect(resize, [props.value, maxHeight]);

    return (
      <textarea
        {...props}
        ref={(element) => {
          innerRef.current = element;
          if (typeof forwardedRef === 'function') forwardedRef(element);
          else if (forwardedRef) forwardedRef.current = element;
        }}
        rows={1}
        onInput={(event) => {
          resize();
          onInput?.(event);
        }}
        style={{ ...style, maxHeight }}
      />
    );
  },
);

AutosizeTextarea.displayName = 'AutosizeTextarea';

export default AutosizeTextarea;
